import {
    HandlingUnit,
    isCargoFreight,
    Shipment,
    ShipmentStatus,
    TransportOrder
} from "@/model/transportorder/transportOrder"
import { IntlShape } from "react-intl"
import {
    calculateVolume, calculateWeight, CsvHeader, downloadCsv,
    toHumanReadableDate,
    toHumanReadableDateAndTime, toHumanReadableTimeWindow
} from "@/components/TransportOrder/CSVExport/csvExportHelpers"
import { getClientTimezone, getTimezoneOfCountry } from "@/timezones/timezones"

interface EmptiesCsvExportRow {
    shipmentExternalIdentification: string | undefined
    externalOrderCreatedDate: string | undefined
    shipmentLastModifiedTime: string | undefined
    shipmentLastModifiedLocation: undefined
    shipmentStatus: string | undefined
    shipFromPlantCode: string | undefined
    shipFromLoLi: string | undefined
    shipFromTimeWindow: string | undefined
    shipFromAddressAddition: string | undefined
    shipFromAccessCode: string | undefined
    transportOrderExternalOrderNumber: string | undefined
    shipFromName: string | undefined
    shipFromAddressStreet: string | undefined
    shipFromAddressPostalCode: string | undefined
    shipFromAddressCity: string | undefined
    shipFromAddressCountry: string | undefined
    shipToDuns: string | undefined
    shipToLoLi: string | undefined
    shipToName: string | undefined
    shipToAddressStreet: string | undefined
    shipToAddressPostalCode: string | undefined
    shipToAddressCity: string | undefined
    shipToAddressCountry: string | undefined
    handlingUnitArticleId: string | undefined
    handlingUnitEmptiesPackagingDetails: string | undefined
    handlingUnitQuantity: string | undefined
    handlingUnitVolume: number | undefined
    handlingUnitWeight: number | undefined
}

export const downloadEmptiesCsv = (
    bundles: TransportOrder[],
    filename: string,
    intl: IntlShape,
): void => {
    const headers: CsvHeader<EmptiesCsvExportRow>[] = [
        {
            key: "shipmentExternalIdentification",
            label: intl.formatMessage({ id: "csvExport.headers.empties.shipmentExternalIdentification" })
        },
        { key: "externalOrderCreatedDate", label: intl.formatMessage({ id: "csvExport.headers.empties.externalOrderCreatedDate" }) },
        { key: "shipmentLastModifiedTime", label: intl.formatMessage({ id: "csvExport.headers.empties.shipmentLastModifiedTime" }) },
        {
            key: "shipmentLastModifiedLocation",
            label: intl.formatMessage({ id: "csvExport.headers.empties.shipmentLastModifiedLocation" })
        },
        { key: "shipmentStatus", label: intl.formatMessage({ id: "csvExport.headers.empties.shipmentStatus" }) },
        { key: "shipFromPlantCode", label: intl.formatMessage({ id: "csvExport.headers.empties.shipFromPlantCode" }) },
        { key: "shipFromLoLi", label: intl.formatMessage({ id: "csvExport.headers.empties.shipFromLoLi" }) },
        { key: "shipFromTimeWindow", label: intl.formatMessage({ id: "csvExport.headers.empties.shipFromTimeWindow" }) },
        { key: "shipFromAddressAddition", label: intl.formatMessage({ id: "csvExport.headers.empties.shipFromAddressAddition" }) },
        { key: "shipFromAccessCode", label: intl.formatMessage({ id: "csvExport.headers.empties.shipFromAccessCode" }) },
        {
            key: "transportOrderExternalOrderNumber",
            label: intl.formatMessage({ id: "csvExport.headers.empties.transportOrderExternalOrderNumber" })
        },
        { key: "shipFromName", label: intl.formatMessage({ id: "csvExport.headers.empties.shipFromName" }) },
        { key: "shipFromAddressStreet", label: intl.formatMessage({ id: "csvExport.headers.empties.shipFromAddressStreet" }) },
        { key: "shipFromAddressPostalCode", label: intl.formatMessage({ id: "csvExport.headers.empties.shipFromAddressPostalCode" }) },
        { key: "shipFromAddressCity", label: intl.formatMessage({ id: "csvExport.headers.empties.shipFromAddressCity" }) },
        { key: "shipFromAddressCountry", label: intl.formatMessage({ id: "csvExport.headers.empties.shipFromAddressCountry" }) },
        { key: "shipToDuns", label: intl.formatMessage({ id: "csvExport.headers.empties.shipToDuns" }) },
        { key: "shipToLoLi", label: intl.formatMessage({ id: "csvExport.headers.empties.shipToLoLi" }) },
        { key: "shipToName", label: intl.formatMessage({ id: "csvExport.headers.empties.shipToName" }) },
        { key: "shipToAddressStreet", label: intl.formatMessage({ id: "csvExport.headers.empties.shipToAddressStreet" }) },
        { key: "shipToAddressPostalCode", label: intl.formatMessage({ id: "csvExport.headers.empties.shipToAddressPostalCode" }) },
        { key: "shipToAddressCity", label: intl.formatMessage({ id: "csvExport.headers.empties.shipToAddressCity" }) },
        { key: "shipToAddressCountry", label: intl.formatMessage({ id: "csvExport.headers.empties.shipToAddressCountry" }) },
        { key: "handlingUnitArticleId", label: intl.formatMessage({ id: "csvExport.headers.empties.handlingUnitArticleId" }) },
        {
            key: "handlingUnitEmptiesPackagingDetails",
            label: intl.formatMessage({ id: "csvExport.headers.empties.handlingUnitEmptiesPackagingDetails" })
        },
        { key: "handlingUnitQuantity", label: intl.formatMessage({ id: "csvExport.headers.empties.handlingUnitQuantity" }) },
        { key: "handlingUnitVolume", label: intl.formatMessage({ id: "csvExport.headers.empties.handlingUnitVolume" }) },
        { key: "handlingUnitWeight", label: intl.formatMessage({ id: "csvExport.headers.empties.handlingUnitWeight" }) },
    ]
    const rows = bundles.flatMap(bundle => mapBundleToCsvData(bundle, intl))
    downloadCsv(rows, headers, filename, intl)
}

export const mapBundleToCsvData = (bundle: TransportOrder, intl: IntlShape): EmptiesCsvExportRow[] => {
    if (!bundle._embedded || !bundle._embedded.shipments) {
        return []
    }
    return bundle._embedded.shipments
        .flatMap(shipment => mapOrderToCsvData(shipment, bundle, intl))
}

const mapOrderToCsvData = (shipment: Shipment, transportOrder: TransportOrder, intl: IntlShape): EmptiesCsvExportRow[] => {
    if (!isCargoFreight(shipment.freight)) {
        return [mapHandlingUnitToCsvData(undefined, shipment, transportOrder, intl)]
    }
    return shipment.freight.handling_units
        .map(handlingUnit => mapHandlingUnitToCsvData(handlingUnit, shipment, transportOrder, intl))
}

const mapHandlingUnitToCsvData = (handlingUnit: HandlingUnit | undefined, shipment: Shipment, transportOrder: TransportOrder, intl: IntlShape): EmptiesCsvExportRow => {
    const currentTransportLeg = shipment.currentTransportLeg
    const startOfTransportChain = shipment.startOfTransportChain
    const endOfTransportChain = shipment.endOfTransportChain

    const shipFromPlantCode = currentTransportLeg.placeOfDeparture.externalIdentifiers.plantCode ??
        (shipment.isStartOfTransportChain ? startOfTransportChain.consignor.externalIdentifiers.plantCode : undefined)
    const shipFromLoli = currentTransportLeg.placeOfDeparture.externalIdentifiers.supplierCode ??
        (shipment.isStartOfTransportChain ? startOfTransportChain.consignor.externalIdentifiers.supplierCode : undefined)
    const shipFromTimeWindow = currentTransportLeg.departureRequestedAt ??
        (shipment.isStartOfTransportChain ? startOfTransportChain.pickupRequestedAt : undefined)
    const shipFromAddress = currentTransportLeg.placeOfDeparture.address ??
        (shipment.isStartOfTransportChain ? startOfTransportChain.consignor.address : undefined)
    const shipFromName = currentTransportLeg.placeOfDeparture.name ??
        (shipment.isStartOfTransportChain ? startOfTransportChain.consignor.name : undefined)

    const shipToAddress = currentTransportLeg.placeOfArrival.address ??
        (shipment.isEndOfTransportChain ? endOfTransportChain.consignee.address : undefined)
    const shipToName = currentTransportLeg.placeOfArrival.name ??
        (shipment.isEndOfTransportChain ? endOfTransportChain.consignee.name : undefined)
    const shipToDuns = currentTransportLeg.placeOfArrival.externalIdentifiers.duns ??
        (shipment.isEndOfTransportChain ? endOfTransportChain.consignee.externalIdentifiers.duns : undefined)
    const shipToLoli = currentTransportLeg.placeOfArrival.externalIdentifiers.supplierCode ??
        (shipment.isEndOfTransportChain ? endOfTransportChain.consignee.externalIdentifiers.supplierCode : undefined)

    return {
        shipmentExternalIdentification: shipment.external_identification,
        externalOrderCreatedDate: toHumanReadableDate(intl.locale, transportOrder.external_order_created_date, getClientTimezone()),
        shipmentLastModifiedTime: toHumanReadableDateAndTime(intl.locale, shipment.last_modified_date, getClientTimezone()),
        shipmentLastModifiedLocation: undefined,
        shipmentStatus: getShipmentStatus(shipment, intl),
        shipFromPlantCode: shipFromPlantCode,
        shipFromLoLi: shipFromLoli,
        shipFromTimeWindow: toHumanReadableTimeWindow(intl.locale, shipFromTimeWindow, getTimezoneOfCountry(shipFromAddress?.country)),
        shipFromAddressAddition: shipment.isStartOfTransportChain ? startOfTransportChain.placeOfLoading : undefined,
        shipFromAccessCode: shipment.isStartOfTransportChain ? startOfTransportChain.accessCode : undefined,
        transportOrderExternalOrderNumber: transportOrder.external_order_number,
        shipFromName: shipFromName,
        shipFromAddressStreet: shipFromAddress?.street,
        shipFromAddressPostalCode: shipFromAddress?.postal_code,
        shipFromAddressCity: shipFromAddress?.city,
        shipFromAddressCountry: shipFromAddress?.country,
        shipToDuns: shipToDuns,
        shipToLoLi: shipToLoli,
        shipToName: shipToName,
        shipToAddressStreet: shipToAddress?.street,
        shipToAddressPostalCode: shipToAddress?.postal_code,
        shipToAddressCity: shipToAddress?.city,
        shipToAddressCountry: shipToAddress?.country,
        handlingUnitArticleId: handlingUnit?.packaging_material_id,
        handlingUnitEmptiesPackagingDetails: undefined,
        handlingUnitQuantity: handlingUnit?.quantity?.toString(),
        handlingUnitVolume: handlingUnit && calculateVolume(handlingUnit),
        handlingUnitWeight: handlingUnit && calculateWeight(handlingUnit)
    }
}

const getShipmentStatus = (shipment: Shipment, intl: IntlShape): string => {
    if (shipment.status === ShipmentStatus.CANCELLED) {
        return intl.formatMessage({ id: "csvExport.values.empties.shipmentStatus.pickupCancelled" })
    } else if (shipment.isConfirmed) {
        return intl.formatMessage({ id: "csvExport.values.empties.shipmentStatus.pickupConfirmed" })
    } else {
        return intl.formatMessage({ id: "csvExport.values.empties.shipmentStatus.pickupRequested" })
    }
}
